import {
  Button,
  Card,
  DataTable,
  EmptyState,
  Icon,
  ListToolbar,
  Text,
  type DataTableColumn,
} from '@ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { PageActions } from '../../../app/PageActions';
import { errorMessage } from '../../../lib/errors';
import { formatDate } from '../../../lib/format';
import { useColumnSort, useSortMenu } from '../../../lib/useColumnSort';
import { useSession } from '../../identity/session';
import { useRowMenu, useLoadedForAction } from '../../../app/rowActions';
import { useMarket, useMarkets, type MarketListItem } from '../api';
import { AssortmentDialog } from '../components/AssortmentDialog';
import { MarketDialog } from '../components/MarketDialog';
import { scheduleText } from '../components/scheduleText';
import { formatEuro } from '../format';
import styles from './SalesPages.module.css';

/** Markets with their schedule: single date, period or recurrence (MKT-003). */
export function MarketsPage() {
  const { t } = useTranslation();
  const { can } = useSession();
  const navigate = useNavigate();
  const markets = useMarkets();
  const columnSort = useColumnSort();
  const [creating, setCreating] = useState(false);
  const write = can('sales.write');
  const create = () => setCreating(true);
  const rowMenu = useRowMenu();
  const [action, setAction] = useState<{ kind: 'edit' | 'assortment'; id: string } | null>(null);

  const columns: DataTableColumn<MarketListItem>[] = [
    {
      key: 'name',
      header: t('sales.markets.name'),
      primary: true,
      cell: (m) => m.name,
      sortValue: (m) => m.name,
    },
    {
      key: 'schedule',
      header: t('sales.markets.kind'),
      mobile: 'subtitle',
      cell: (m) => scheduleText(t, m.schedule),
    },
    {
      key: 'place',
      header: t('sales.markets.place'),
      cell: (m) => m.place ?? null,
      sortValue: (m) => m.place,
    },
    {
      key: 'next',
      header: t('sales.markets.next'),
      mobile: 'hidden',
      cell: (m) => (m.next ? formatDate(m.next) : null),
      sortValue: (m) => m.next,
      width: '8rem',
    },
    {
      key: 'days',
      header: t('sales.markets.daysTitle'),
      align: 'end',
      mobile: 'hidden',
      cell: (m) => (
        <Text as="span" numeric>
          {m.days}
        </Text>
      ),
      sortValue: (m) => m.days,
    },
    {
      key: 'total',
      header: t('sales.columns.total'),
      align: 'end',
      cell: (m) => (
        <Text as="span" numeric>
          {formatEuro(m.total_cents)}
        </Text>
      ),
      sortValue: (m) => m.total_cents,
    },
  ];

  const sortMenu = useSortMenu(columns, markets.data ?? [], columnSort);

  return (
    <div className={styles.page}>
      {write && (
        <PageActions>
          <Button iconStart={<Icon name="plus" size="sm" />} onClick={create}>
            {t('sales.markets.create')}
          </Button>
        </PageActions>
      )}
      <ListToolbar sort={sortMenu} />
      <Card padding="none">
        {markets.error ? (
          <Text tone="danger" role="alert" style={{ padding: 'var(--space-4)' }}>
            {errorMessage(markets.error)}
          </Text>
        ) : (
          <DataTable
            caption={t('sales.markets.caption')}
            rows={markets.data ?? []}
            rowKey={(m) => m.id}
            rowHref={(m) => `/verkauf/maerkte/${m.id}`}
            rowActions={rowMenu.table<MarketListItem>(
              (m) => m.name,
              (m) =>
                write
                  ? [
                      {
                        key: 'assortment',
                        label: t('sales.assortment.edit'),
                        onSelect: () => setAction({ kind: 'assortment', id: m.id }),
                      },
                      rowMenu.edit(() => setAction({ kind: 'edit', id: m.id })),
                    ]
                  : [],
            )}
            {...columnSort}
            loading={markets.isPending}
            columns={columns}
            empty={
              <EmptyState
                title={t('sales.markets.emptyTitle')}
                description={t('sales.markets.emptyText')}
                actions={write && <Button onClick={create}>{t('sales.markets.create')}</Button>}
              />
            }
          />
        )}
      </Card>
      {creating && (
        <MarketDialog
          onClose={() => setCreating(false)}
          onSaved={(m) => void navigate(`/verkauf/maerkte/${m.id}`)}
        />
      )}
      {action && <LoadedMarket {...action} onClose={() => setAction(null)} />}
    </div>
  );
}

/** Loads the market, then shows its edit or assortment dialog. */
function LoadedMarket({
  kind,
  id,
  onClose,
}: {
  kind: 'edit' | 'assortment';
  id: string;
  onClose: () => void;
}) {
  const market = useLoadedForAction(useMarket(id), onClose);
  if (!market) return null;
  return kind === 'edit' ? (
    <MarketDialog market={market} onClose={onClose} />
  ) : (
    <AssortmentDialog market={market} onClose={onClose} />
  );
}
